'use client'

import { LOGIN } from '@/app/app.config'
import { LOGIN_TYPE } from '@/constants/data'
import { AUTH_TOKEN_KEY, OAUTH_TOKEN_KEY } from '@/constants/keys'
import { APP_ROUTES, getRedirectPage, isWithAuthPage } from '@/constants/routes'
import { useWebView } from '@/hooks/useWebView'
import { queryKeys } from '@/service/queries'
import { useGetMe } from '@/service/queries/useUser'
import { ITokenPayload } from '@/types'
import { IUser } from '@/types/interfaces/user'
import { useQueryClient } from '@tanstack/react-query'
import { deleteCookie, getCookie, setCookie } from 'cookies-next'
import { jwtDecode } from 'jwt-decode'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

type AuthContextType = {
  userData: IUser | undefined
  tokenPayload: ITokenPayload | null
  isLoading: boolean
  isLogin: boolean
  setDefaultLogin: (token: string) => Promise<string | undefined>
  setLogout: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

/**
 * 사용자 인증 context provider
 *
 * 로그인 사용자 정보조회, 로그아웃 처리
 */
export default function AuthContextProvider({
  defaultHasToken,
  children,
}: {
  defaultHasToken?: boolean
  children: ReactNode
}) {
  const router = useRouter()
  const pathname = usePathname()
  const params = useSearchParams()
  const queryClient = useQueryClient()
  const { webviewEvents } = useWebView()

  const [token, setToken] = useState(
    LOGIN.enabled && defaultHasToken ? getCookie(AUTH_TOKEN_KEY) : undefined
  )

  const tokenPayload = useMemo(() => {
    if (!token) return null

    try {
      return jwtDecode<ITokenPayload>(token)
    } catch (e) {
      return null
    }
  }, [token])

  const { data: userData, isLoading, isStale, isError, refetch: userRefetch } = useGetMe(!!token)

  const isLogin = useMemo(() => !isLoading && !!token && !!userData, [isLoading, token, userData])

  /**
   * 인증 데이터 무력화
   *
   * - 개인화 데이터관련 처리
   */
  const initAuthData = useCallback(() => {
    // 데이터 삭제
    queryClient.removeQueries({ queryKey: queryKeys.USER.all })
    queryClient.removeQueries({ queryKey: queryKeys.ORDER.all })
    queryClient.removeQueries({ queryKey: queryKeys.ORDER_OPTION.all })
    queryClient.removeQueries({ queryKey: queryKeys.ORDER_CLAIM.all })
    queryClient.removeQueries({ queryKey: queryKeys.DELIVERY_ADDRESS.all })
    queryClient.removeQueries({ queryKey: queryKeys.PRODUCT_REVIEWS.ownList() })

    // 데이터 갱신
    queryClient.invalidateQueries({ queryKey: queryKeys.POSTS.all })
  }, [queryClient])

  // 인증상태만 접근 가능한 페이지 확인
  const checkAuthPage = useCallback(() => {
    if (isWithAuthPage(pathname)) {
      const redirect = encodeURIComponent(`${pathname}?${params.toString()}`)
      router.push(`${APP_ROUTES.LOGIN.href}?redirect=${redirect}`)
    }
  }, [pathname, params, router])

  const doLogout = useCallback(
    (withRedirect = true) => {
      deleteCookie(AUTH_TOKEN_KEY)
      setToken(undefined)
      initAuthData()
      withRedirect && checkAuthPage()
      // 앱 로그아웃 처리
      webviewEvents.logout()
    },
    [webviewEvents, initAuthData, checkAuthPage]
  )

  const setLogout = useCallback(() => {
    doLogout()
    // confirmModal({
    //   title: 'Are you sure you want to log out?',
    //   rightBtn: { callback: doLogout },
    // })
  }, [doLogout])

  const setDefaultLogin = useCallback(
    async (token: string) => {
      setCookie(AUTH_TOKEN_KEY, token)
      setToken(token)

      const { data } = await userRefetch()

      if (!data) {
        throw new Error('Login failure, try again')
      } else if (data.authType !== LOGIN_TYPE.DEFAULT) {
        throw new Error('Is Not Email Login')
      } else {
        initAuthData()
        deleteCookie(OAUTH_TOKEN_KEY)

        return getRedirectPage()
      }
    },
    [userRefetch, initAuthData]
  )

  useEffect(() => {
    if (isError) {
      // console.error('Auth Error do logout')
      doLogout()
    }
  }, [isError, doLogout])

  useEffect(() => {
    if (isStale) {
      userRefetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStale])

  return (
    <AuthContext.Provider
      value={{ userData, tokenPayload, isLoading, isLogin, setDefaultLogin, setLogout }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuthContext() {
  const context = useContext(AuthContext)

  if (context === null) {
    throw new Error('useAuthContext는 AuthContextProvider내에서만 사용가능합니다.')
  }
  return context
}
